import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Container,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Grid } from '@mui/system';
import Fuse from 'fuse.js';
import { isEmpty } from 'lodash';
import {
  CANCEL,
  OTHER,
  SELECT_WITH_WHICH_DOCUMENT_REQUEST,
  SELECTION_MODAL_DESCRIPTION,
  UPLOAD,
} from 'common/constants/process-management';
import { FileToUpload, RequestedFileExtended } from 'common/types/files';
import { SecurePortalParams } from 'common/types/reactRouter';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { TaskContext } from 'context/TaskContext';
import theme from 'theme';
import { SelectedFile, SelectionModalProps } from './SelectionModal.props';
import { selectionModalClasses, StyledSelectionModalDialog } from './SelectionModal.styles';



const SelectionModal = ({
  open,
  setOpenSelectionModal,
  requestedFiles,
  filesToUpload,
  setUploadedFiles,
  uploadRequestedFiles,
  uploadToOtherRequestedFile,
  taskPortalLinkData = {} as SecurePortalParams,
  companyDocuments,
}: SelectionModalProps) => {
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const { task: taskFromTaskContext, validateToken } = useContext(TaskContext) || {};
  const { task: taskFromInformationDashboard409AContext } = useContext(InformationDashboard409AContext) || {};
  const task = taskFromTaskContext ?? taskFromInformationDashboard409AContext;
  const [allFilesAreSimilar, setAllFilesAreSimilar] = useState(false);
  const [requestedFileChoices, setRequestedFileChoices] = useState<any[]>([]);

  const checkIfOtherRequestedFileExist = useCallback(() => {
    const otherRequestedFile = requestedFiles?.find(item => item.name === OTHER);
    return otherRequestedFile;
  }, [requestedFiles]);

  const handleClose = useCallback(() => {
    setOpenSelectionModal(false);
  }, [setOpenSelectionModal]);

  const addRequestedFile = (
    fileToUpload: FileToUpload,
    requestedFile: RequestedFileExtended,
    selectedFilesParam: SelectedFile[]
  ) => {
    const selectedFile = selectedFilesParam.find(item => item.fileToUpload?.tmpId === fileToUpload.tmpId);
    if (selectedFile) {
      selectedFile.requestedFiles?.push(requestedFile);
      return [...selectedFilesParam];
    }
    return [...selectedFilesParam, { fileToUpload, requestedFiles: [requestedFile] }];
  };

  const removeRequestedFile = (
    fileToUpload: FileToUpload,
    requestedFile: RequestedFileExtended,
    selectedFilesParam: SelectedFile[]
  ) => {
    const selectedFile = selectedFilesParam.find(item => item.fileToUpload.tmpId === fileToUpload.tmpId);
    if (selectedFile) {
      const requestedFileIndex = selectedFile.requestedFiles?.findIndex(item => item.id === requestedFile.id);
      if (requestedFileIndex !== -1 && requestedFileIndex) {
        selectedFile.requestedFiles?.splice(requestedFileIndex, 1);
      }
      return [...selectedFilesParam];
    }
    return [...selectedFilesParam];
  };

  const updateSelectedFiles = useCallback(
    (event, fileToUpload: FileToUpload, requestedFile: RequestedFileExtended, selectedFilesParam: SelectedFile[]) => {
      if (event.target.checked) {
        return addRequestedFile(fileToUpload, requestedFile, selectedFilesParam);
      }
      return removeRequestedFile(fileToUpload, requestedFile, selectedFilesParam);
    },
    []
  );

  const handleCheckboxChange = useCallback(
    (event, fileToUpload: FileToUpload, requestedFile: RequestedFileExtended) => {
      const newSelectedFiles = updateSelectedFiles(event, fileToUpload, requestedFile, selectedFiles);
      setSelectedFiles(newSelectedFiles);
    },
    [selectedFiles, updateSelectedFiles]
  );

  const isSimilarName = useCallback((fileToUpload, requestedFile) => {
    const list = [requestedFile.name, fileToUpload.file.name];
    const options = {
      includeScore: true,
      threshold: 0.55,
    };
    const fuse = new Fuse(list, options);
    const result = fuse.search(fileToUpload.file.name);

    return result.length > 1;
  }, []);

  const handleUpload = useCallback(() => {
    handleClose();

    if (selectedFiles.length === 0) {
      uploadToOtherRequestedFile();
    }

    selectedFiles.forEach(async file => {
      const { fileToUpload, requestedFiles: tmpRequestedFiles } = file;
      const { requestId, hmacToken } = taskPortalLinkData || {};
      const requestedFilesIds = tmpRequestedFiles?.map(item => item.id) as number[];

      await uploadRequestedFiles?.({
        droppedFile: fileToUpload.file,
        requestedFilesIds,
        task,
        setUploadedFiles,
        requestId,
        hmacToken,
        files: companyDocuments ?? [],
      });
      if (validateToken) {
        validateToken();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, selectedFiles, setUploadedFiles, task, uploadRequestedFiles, uploadToOtherRequestedFile]);

  useEffect(() => {
    if (isEmpty(filesToUpload)) {
      return;
    }
    setAllFilesAreSimilar(
      filesToUpload?.every(fileToUpload =>
        requestedFiles?.some(requestedFile => isSimilarName(fileToUpload, requestedFile))
      ) ?? false
    );
  }, [filesToUpload, handleClose, isSimilarName, requestedFiles]);

  useEffect(() => {
    if (allFilesAreSimilar) {
      handleClose();
      const newSelectedFiles: SelectedFile[] = [];
      filesToUpload?.forEach(fileToUpload => {
        const tmpRequestedFiles: RequestedFileExtended[] = [];
        tmpRequestedFiles.forEach(requestedFile => {
          if (isSimilarName(fileToUpload, requestedFile)) {
            tmpRequestedFiles.push(requestedFile);
          }
        });
        newSelectedFiles.push({ fileToUpload, tmpRequestedFiles });
      });
      setSelectedFiles(newSelectedFiles);
      handleUpload();
    }
  }, [allFilesAreSimilar, filesToUpload, handleClose, handleUpload, isSimilarName]);

  useEffect(() => {
    const newSelectedFiles: SelectedFile[] = [];
    if (isEmpty(filesToUpload)) {
      return;
    }
    filesToUpload?.forEach(fileToUpload => {
      requestedFiles?.forEach(requestedFile => {
        if (isSimilarName(fileToUpload, requestedFile)) {
          newSelectedFiles.push({
            fileToUpload,
            requestedFiles: [requestedFile],
          });
        }
      });
    });
    setSelectedFiles(newSelectedFiles);
  }, [filesToUpload, requestedFiles, isSimilarName]);

  useEffect(() => {
    if (!checkIfOtherRequestedFileExist()) {
      setRequestedFileChoices([...(requestedFiles ?? []), { id: OTHER, name: OTHER }]);
    } else {
      setRequestedFileChoices(requestedFiles ?? []);
    }
  }, [checkIfOtherRequestedFileExist, requestedFiles]);

  useEffect(() => {
    if (task?.task?.is_done) {
      handleClose();
      uploadToOtherRequestedFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledSelectionModalDialog fullScreen={fullScreen} open={open} onClose={handleClose}>
      <Container style={{ marginTop: '3rem' }}>
        <DialogContentText className={selectionModalClasses.textDescription}>
          {SELECTION_MODAL_DESCRIPTION}
        </DialogContentText>
      </Container>
      {filesToUpload?.map(file => (
        <div key={file.tmpId}>
          <DialogTitle
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {file.file.name}
          </DialogTitle>
          <DialogContent>
            <DialogContentText className={selectionModalClasses.textDescription}>
              {SELECT_WITH_WHICH_DOCUMENT_REQUEST}
            </DialogContentText>
            <Grid container spacing={2}>
              {requestedFileChoices.map(item => (
                <Grid key={item.id} size={{ xs: 6}}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        className={selectionModalClasses.checkbox}
                        onChange={event => handleCheckboxChange(event, file, item)}
                        checked={
                          !allFilesAreSimilar
                          && selectedFiles.some(
                            selectedFile =>
                              selectedFile.fileToUpload.tmpId === file.tmpId
                              && selectedFile.requestedFiles?.some(requestedFile => requestedFile.id === item.id)
                          )
                        }
                      />
                    }
                    label={<Typography className={selectionModalClasses.checkboxLabel}>{item.name}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          {file.tmpId !== filesToUpload[filesToUpload.length - 1].tmpId && <Divider style={{ margin: '1rem 0' }} />}
        </div>
      ))}
      <DialogActions className={selectionModalClasses.actionButtonsContainer}>
        <Button
          size="large"
          variant="contained"
          onClick={handleUpload}
          color="primary"
          className={selectionModalClasses.actionButton}
          style={{ marginRight: '0.5rem' }}>
          {UPLOAD}
        </Button>
        <Button
          size="large"
          variant="outlined"
          onClick={handleClose}
          color="primary"
          className={selectionModalClasses.actionButton}>
          {CANCEL}
        </Button>
      </DialogActions>
    </StyledSelectionModalDialog>
  );
};

export default SelectionModal;
